/**
 * Lot 35 — Madrassa-admin tab components: Teachers / Students lists with
 * stats. Used by the existing manage view to give a quick supervision UX.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Search, RefreshCcw, GraduationCap, ClipboardList, AlertTriangle,
  CheckCircle2, XCircle, Mail,
} from 'lucide-react';
import { GlassCard } from './shared';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { madrassaApi } from '../services/api';

const SUPPORT_META = {
  on_track: { fr: 'En forme', icon: CheckCircle2, cls: 'text-emerald-300' },
  needs_reinforcement: { fr: 'À renforcer', icon: AlertTriangle, cls: 'text-amber-300' },
  needs_help: { fr: 'À aider', icon: XCircle, cls: 'text-rose-300' },
};
const fmtDate = (s) => (s ? String(s).slice(0, 10) : '—');

const MembershipStatusBadge = ({ status }) => {
  if (!status) return null;
  const cls = status === 'approved' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
    : status === 'pending' ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
    : 'bg-rose-500/15 text-rose-300 border-rose-500/30';
  return <Badge className={`${cls} border text-[10px]`}>{status}</Badge>;
};

const PersonRow = ({ u, classes, role, testidPrefix }) => {
  const supportMeta = SUPPORT_META[u.supportStatus];
  const Icon = supportMeta?.icon;
  const a = u.assignmentStats || {};
  const p = u.progressSummary || {};
  return (
    <GlassCard data-testid={`${testidPrefix}-row-${u.userId}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-9 h-9 rounded-md bg-primary/15 text-primary flex items-center justify-center shrink-0">
          {role === 'teacher' ? <Mail className="w-4 h-4" /> : <GraduationCap className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-text-primary truncate"
                  data-testid={`${testidPrefix}-row-${u.userId}-name`}>
              {u.name || u.email || u.userId}
            </span>
            <MembershipStatusBadge status={u.membershipStatus} />
            {supportMeta && Icon && (
              <span className={`inline-flex items-center gap-1 text-[11px] ${supportMeta.cls}`}>
                <Icon className="w-3 h-3" /> {supportMeta.fr}
              </span>
            )}
          </div>
          <p className="text-[11px] text-text-muted mt-0.5">{u.email}</p>
          {(u.classes || []).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {u.classes.map((c) => (
                <Badge key={c.classId}
                       className="bg-surface-elevated text-text-secondary border border-border text-[10px]">
                  {c.className} ({c.role})
                </Badge>
              ))}
            </div>
          )}
          <div className="flex items-center gap-3 mt-2 text-[11px] text-text-secondary flex-wrap">
            <span><ClipboardList className="w-3 h-3 inline" /> {a.completed ?? 0}/{a.assigned ?? 0} devoirs</span>
            <span>Score moyen : {a.averageScore != null ? `${a.averageScore}%` : '—'}</span>
            {role === 'student' && (
              <>
                <span>Lecture : {p.readingProgressPercent != null ? `${p.readingProgressPercent}%` : '—'}</span>
                <span>Tajwīd : {p.tajweedProgressPercent != null ? `${p.tajweedProgressPercent}%` : '—'}</span>
              </>
            )}
            {u.lastActivityAt && (
              <span>Activité : {fmtDate(u.lastActivityAt)}</span>
            )}
          </div>
        </div>
      </div>
    </GlassCard>
  );
};

/**
 * Generic tab body for Teachers (role='teacher') / Students (role='student').
 */
export const MadrassaUsersTab = ({ madrassaId, role, classes = [],
                                   testidPrefix = 'madrassa-users' }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [classId, setClassId] = useState('all');
  const [status, setStatus] = useState('all');

  const fetcher = role === 'teacher' ? madrassaApi.listTeachers
    : role === 'student' ? madrassaApi.listStudents
    : madrassaApi.listUsers;

  const refresh = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (classId !== 'all') params.class_id = classId;
      if (status !== 'all') params.status = status;
      const r = await fetcher(madrassaId, params);
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [madrassaId, role]);

  const filtered = useMemo(() => items, [items]);

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-tab`}>
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => { e.preventDefault(); refresh(); }}
      >
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
          <Input
            placeholder="Rechercher par nom ou email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-8"
            data-testid={`${testidPrefix}-search`}
          />
        </div>
        <Select value={classId} onValueChange={(v) => { setClassId(v); }}>
          <SelectTrigger className="w-[170px]"
                         data-testid={`${testidPrefix}-filter-class`}>
            <SelectValue placeholder="Classe" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes classes</SelectItem>
            {classes.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => { setStatus(v); }}>
          <SelectTrigger className="w-[150px]"
                         data-testid={`${testidPrefix}-filter-status`}>
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="approved">Approuvés</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
            <SelectItem value="rejected">Rejetés</SelectItem>
          </SelectContent>
        </Select>
        <Button type="submit" size="sm" data-testid={`${testidPrefix}-search-submit`}>
          <Search className="w-3.5 h-3.5 mr-1" /> Rechercher
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={refresh}
                data-testid={`${testidPrefix}-refresh`}>
          <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Rafraîchir
        </Button>
      </form>

      {loading && (
        <p className="text-xs text-text-muted"
           data-testid={`${testidPrefix}-loading`}>Chargement…</p>
      )}
      {!loading && filtered.length === 0 && (
        <GlassCard data-testid={`${testidPrefix}-empty`}>
          <p className="text-sm text-text-muted text-center py-3">
            Aucun {role === 'teacher' ? 'professeur' : 'élève'} pour ce filtre.
          </p>
        </GlassCard>
      )}
      {!loading && filtered.map((u) => (
        <PersonRow key={u.userId} u={u} role={role} testidPrefix={testidPrefix} />
      ))}
    </div>
  );
};

export default MadrassaUsersTab;
